import type { TreeItemType } from '@workramp/ui';
import type { LibraryFolder } from '../../api/getLibraryFolder';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { Icon } from '@workramp/ui';

import { I18n } from '~/common/explicit-exports';

import { useGetLibraries } from '../../api/getLibraries';
import { useGetLibraryFolder } from '../../api/getLibraryFolder';
import { InvalidKeys } from './constants';

type UseGetLibraryFolderProps = {
  libraryId?: string;
  expandedKeys: Set<string | number>;
};

const LibraryIcon = () => {
  const theme = useTheme();
  return <Icon color={theme.colors.decorative.purple[500]} name="library:regular" />;
};

const FolderCloseIcon = () => {
  const theme = useTheme();
  return <Icon color={theme.colors.on[500]} name="folder-closed:regular" />;
};
const FolderOpenIcon = () => {
  const theme = useTheme();
  return <Icon color={theme.colors.on[500]} name="folder-open:regular" />;
};

const AddIcon = () => {
  const theme = useTheme();
  return <Icon color={theme.colors.on[600]} name="add:regular" />;
};

/*
 * given an array of library folders, return a tree data structure
 */

export type LibraryTreeItemType = TreeItemType & {
  parentUniversityFolderId?: string | null;
  libraryId: string;
  folderId?: string;
};
const transformToTreeData = (
  libraryFolders: LibraryFolder[],
  expandedKeys: Set<string | number>,
  libraryId: string,
): LibraryTreeItemType[] => {
  const treeData: LibraryTreeItemType[] = [];
  const libraryFoldersMap = new Map<string | number, LibraryTreeItemType>();

  const libraryTreeItems: Array<LibraryTreeItemType> = libraryFolders
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((libraryFolder) => ({
      ...libraryFolder,
      libraryId,
      folderId: libraryFolder.id,
      icon: expandedKeys.has(libraryFolder.id) ? FolderOpenIcon : FolderCloseIcon,
    }));

  libraryTreeItems.forEach((libraryTreeItem) => {
    libraryFoldersMap.set(libraryTreeItem.id as string, libraryTreeItem);
  });

  libraryTreeItems.forEach((libraryTreeItem) => {
    const parentLibraryFolder = libraryFoldersMap.get(libraryTreeItem.parentUniversityFolderId);

    if (parentLibraryFolder) {
      if (!parentLibraryFolder.children) {
        parentLibraryFolder.children = [];
      }
      if (parentLibraryFolder.children.find((childFolder) => childFolder.id === libraryTreeItem.id)) {
        return;
      }
      parentLibraryFolder.children.push(libraryTreeItem);
    } else {
      treeData.push(libraryTreeItem);
    }
  });
  return treeData;
};
/**
 * This hook is used to get the library folder tree data
 * if a libraryId is passed in, it will fetch the library folder tree data for that library
 * @param param0
 * @returns
 */
export const useGetLibrariesFolderTree = ({ libraryId, expandedKeys }: UseGetLibraryFolderProps) => {
  const libaryFolderQuery = useGetLibraryFolder(libraryId);
  const libariesQuery = useGetLibraries();
  const LOADING = useMemo(() => I18n.t('js.admin.cms.libraries.loading'), []);
  const ERROR = useMemo(() => I18n.t('js.admin.cms.libraries.error'), []);
  const ADD_TO_LIBRARY = useMemo(() => I18n.t('js.admin.cms.libraries.add_to_library'), []);

  const [treeData, setTreeData] = useState<LibraryTreeItemType[]>([]);
  const treeRef = useRef<LibraryTreeItemType[]>(treeData);
  treeRef.current = treeData;

  const folderTreeData = useMemo(() => {
    if (!libaryFolderQuery.data) {
      return [];
    }
    return transformToTreeData(libaryFolderQuery.data, expandedKeys, libraryId);
  }, [libaryFolderQuery.data, expandedKeys, libraryId]);

  useEffect(() => {
    if (!libariesQuery.data) {
      return;
    }
    if (!treeRef.current.length) {
      setTreeData(
        libariesQuery.data.map((library) => ({
          id: library.id,
          libraryId: library.id,
          name: library.name,
          icon: LibraryIcon,
          children: [
            {
              id: InvalidKeys.loadingKey,
              name: LOADING,
            },
          ],
        })),
      );
    } else {
      setTreeData((prevTreeData) =>
        prevTreeData.map((treeItem) => {
          if (treeItem.id === libraryId) {
            if (libaryFolderQuery.isInitialLoading) {
              return {
                ...treeItem,
                children: [
                  {
                    id: InvalidKeys.loadingKey,
                    name: LOADING,
                  },
                ],
              };
            }
            if (libaryFolderQuery.isError) {
              return {
                ...treeItem,
                children: [
                  {
                    id: InvalidKeys.errorKey,
                    name: ERROR,
                  },
                ],
              };
            }
            if (libaryFolderQuery.isSuccess && libaryFolderQuery.data && libaryFolderQuery.data.length === 0) {
              return {
                ...treeItem,
                children: [
                  {
                    id: `${InvalidKeys.emptyKey}_${libraryId}`,
                    name: ADD_TO_LIBRARY,
                    libraryId,
                    folderId: libariesQuery.data.find((library) => library.id === libraryId)?.folderId,
                    icon: AddIcon,
                  },
                ],
              };
            }
            return { ...treeItem, children: folderTreeData };
          }
          return treeItem;
        }),
      );
    }
  }, [
    folderTreeData,
    libraryId,
    libariesQuery.data,
    libaryFolderQuery.isInitialLoading,
    libaryFolderQuery.isError,
    libaryFolderQuery.isSuccess,
    libaryFolderQuery.data,
    ERROR,
    LOADING,
    ADD_TO_LIBRARY,
  ]);

  if (libariesQuery.isLoading || libariesQuery.isError) {
    return treeData;
  }

  return treeData;
};
